import dayjs from "dayjs";
import { EventTime } from "~/api/resources/Events";
import {
  fetchListingsByType,
  ListingsByTypeQueryParams,
} from "~/api/resources/Listings";
import { debounce, merge } from "lodash";
import { useCallback, useRef } from "react";

export const getValidUrl = (url = "") => {
  const newUrl = url.trim().replace(/\s/g, "");
  if (/^(:\/\/)/.test(newUrl)) {
    return `http${newUrl}`;
  }
  if (!/^(f|ht)tps?:\/\//i.test(newUrl)) {
    return `https://${newUrl}`;
  }
  return newUrl;
};

export const getDisplayUrl = (url = "") => {
  return url
    .replace(/(^\w+:|^)\/\//, "") //remove protocol
    .replace(/\?.*/, "") //remove query params
    .replace(/\/$/, "") //remove trailing slash
    .replace("www.", ""); //remove www.
};

export const nonNullable = <T>(value: T): value is NonNullable<T> =>
  value !== null && value !== undefined;

export const filterObjectByKey = <T extends object>(
  object: T,
  key: keyof T
) => {
  return Object.keys(object)
    .filter((k) => {
      k !== key;
    })
    .reduce((result, k) => {
      result[k as keyof T] = object[k as keyof T];
      return result;
    }, {} as T);
};

export const formatEventTimeDates = (event_time: EventTime) => {
  const { start, end, all_day } = event_time;
  const start_date = dayjs(start);
  const end_date = dayjs(end);
  const isSameDay = start_date.isSame(end_date, "day");
  const start_string = start_date.format(
    isSameDay || all_day ? "MMMM D" : "MMMM D h:mm A"
  );
  const end_string =
    !isSameDay && end_date.format(all_day ? "MMMM D" : "MMMM D h:mm A");
  const start_time_string = start_date.format("h:mm A");
  const end_time_string = end_date.format("h:mm A");
  const date_line = [start_string, end_string].filter(Boolean).join(" - ");
  const time_line =
    isSameDay && !all_day && [start_time_string, end_time_string].join(" - ");
  return { date_line, time_line };
};

type ObjectLiteral = { [key: string]: any };

export const useDebouncedFilter = <FParams extends ObjectLiteral>(
  callback: (filter: FParams) => void
) => {
  const filterRef = useRef<FParams>({} as FParams);

  const debouncedSearch = useRef(
    debounce(async () => {
      callback(filterRef.current);
    }, 400)
  ).current;
  const updateFilter = useCallback(
    <Ids, Key extends keyof FParams>(key: Key, items: { id: Ids }[]) => {
      const value = items.map(({ id }) => id);
      const state = filterRef.current;
      filterRef.current =
        !value || (Array.isArray(value) && !value.length)
          ? filterObjectByKey(state, key)
          : { ...state, [key]: value };

      debouncedSearch();
    },
    [debouncedSearch]
  );

  return { debouncedSearch, updateFilter, filterRef };
};

export const toggleArrayValue = <T>(array: T[], value: T): T[] => {
  return array.includes(value)
    ? array.filter((item) => item !== value)
    : [...array, value];
};

export const covertToArray = <T>(value: T | T[] | undefined) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export const sortAlphaByKey = <
  Key extends string,
  Array extends { [key in Key]: string }[],
>(
  key: Key,
  array: Array
) => {
  return array.sort((a, b) =>
    a[key].toLowerCase().localeCompare(b[key].toLowerCase())
  );
};
export const getStakeholdersByType = (params?: ListingsByTypeQueryParams) => {
  const stakeholder_filter: ListingsByTypeQueryParams = {
    and: { tags: { category: "stakeholder-type", name: "Stakeholder" } },
  };
  return fetchListingsByType(merge({}, stakeholder_filter, params));
};
